/**
 * Quick periods — the same list as Turbo's PERIOD_PRESETS (donationsReport/drUtils.js), as from / to (YYYY-MM-DD).
 * Hebrew years (1 Tishrei – the eve of the next Rosh Hashana) come from the Intl Hebrew calendar, no dependency.
 */

const pad = (n: number) => String(n).padStart(2, "0");
export const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export type Period = { from: string; to: string };
export type PeriodPreset = { value: string; label: string | (() => string); period: (today: Date) => Period };

let hebFormat: Intl.DateTimeFormat | null = null;
/** the Hebrew year a (local) calendar day falls in */
export function hebrewYearOf(d: Date): number {
  if (!hebFormat) hebFormat = new Intl.DateTimeFormat("en-u-ca-hebrew", { year: "numeric", timeZone: "UTC" });
  const part = hebFormat.formatToParts(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12))).find((p) => p.type === "year");
  return Number(part?.value);
}

/** 1 Tishrei of a Hebrew year (Rosh Hashana falls between Sept 5 and Oct 5) */
export function roshHashana(hebYear: number): Date {
  const y = hebYear - 3761;
  for (let i = 0; i < 45; i += 1) {
    const d = new Date(y, 8, 1 + i);
    if (hebrewYearOf(d) === hebYear) return d;
  }
  return new Date(y, 8, 1);
}

/** a Hebrew year as from / to, `offset` years from the one `today` is in */
export function hebrewYearPeriod(offset: number, today = new Date()): Period & { y: number } {
  const y = hebrewYearOf(today) + offset;
  const start = roshHashana(y);
  const next = roshHashana(y + 1);
  const end = new Date(next.getFullYear(), next.getMonth(), next.getDate() - 1);
  return { y, from: ymd(start), to: ymd(end) };
}

const HUNDREDS: Array<[number, string]> = [[400, "ת"], [300, "ש"], [200, "ר"], [100, "ק"]];
const TENS = ["", "י", "כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ"];
const UNITS = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
/** 5787 → תשפ״ז (thousands omitted; 15 / 16 as ט״ו / ט״ז) */
export function gematriya(n: number): string {
  let rest = n % 1000;
  let out = "";
  for (const [v, ch] of HUNDREDS) {
    while (rest >= v) {
      out += ch;
      rest -= v;
    }
  }
  if (rest === 15) out += "טו";
  else if (rest === 16) out += "טז";
  else out += TENS[Math.floor(rest / 10)] + UNITS[rest % 10];
  return out.length > 1 ? `${out.slice(0, -1)}״${out.slice(-1)}` : `${out}׳`;
}

/** a period that has not ended — up to the end of this month */
const capToThisMonth = (to: string, t: Date) => {
  const end = ymd(new Date(t.getFullYear(), t.getMonth() + 1, 0));
  return to > end ? end : to;
};

export const PERIOD_PRESETS: PeriodPreset[] = [
  { value: "this-month", label: "החודש", period: (t) => ({ from: ymd(new Date(t.getFullYear(), t.getMonth(), 1)), to: ymd(t) }) },
  {
    value: "last-month",
    label: "החודש הקודם",
    period: (t) => ({ from: ymd(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: ymd(new Date(t.getFullYear(), t.getMonth(), 0)) }),
  },
  {
    value: "last-12",
    label: "12 החודשים האחרונים",
    period: (t) => ({ from: ymd(new Date(t.getFullYear(), t.getMonth() - 11, 1)), to: ymd(new Date(t.getFullYear(), t.getMonth() + 1, 0)) }),
  },
  { value: "this-year", label: "השנה", period: (t) => ({ from: `${t.getFullYear()}-01-01`, to: capToThisMonth(`${t.getFullYear()}-12-31`, t) }) },
  { value: "last-year", label: "השנה שעברה", period: (t) => ({ from: `${t.getFullYear() - 1}-01-01`, to: `${t.getFullYear() - 1}-12-31` }) },
  {
    value: "heb-year",
    label: () => `שנת ${gematriya(hebrewYearPeriod(0).y)}`,
    period: (t) => {
      const h = hebrewYearPeriod(0, t);
      return { from: h.from, to: capToThisMonth(h.to, t) };
    },
  },
  {
    value: "prev-heb-year",
    label: () => `שנת ${gematriya(hebrewYearPeriod(-1).y)}`,
    period: (t) => {
      const h = hebrewYearPeriod(-1, t);
      return { from: h.from, to: h.to };
    },
  },
  { value: "last-5-years", label: "5 השנים האחרונות", period: (t) => ({ from: `${t.getFullYear() - 4}-01-01`, to: ymd(t) }) },
];

export const presetLabel = (p: PeriodPreset) => (typeof p.label === "function" ? p.label() : p.label);

/** the presets with today's from / to, for a Select */
export function periodOptions(today = new Date()): Array<{ value: string; label: string } & Period> {
  return PERIOD_PRESETS.map((p) => ({ value: p.value, label: presetLabel(p), ...p.period(today) }));
}

/** the preset that matches from / to exactly ("" = a custom range) */
export function matchPreset(period: Partial<Period>, today = new Date()): string {
  return periodOptions(today).find((p) => p.from === period.from && p.to === period.to)?.value ?? "";
}

/** dd/mm/yyyy – dd/mm/yyyy */
export function periodText(p: Partial<Period>): string {
  const he = (s?: string) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : "…");
  if (!p.from && !p.to) return "";
  return p.from === p.to ? he(p.from) : `${he(p.from)} – ${he(p.to)}`;
}
